import { useMemo, useState, type FormEvent } from "react";

import { formatMinor, parseRubles, todayLocal } from "../lib/money";
import { missingField } from "../lib/parser";
import type { AccountBalance, Category, IncomeSource, OperationDraft } from "../lib/types";

interface OperationModalProps {
  initialDraft: OperationDraft;
  transactionId?: string;
  accounts: AccountBalance[];
  categories: Category[];
  incomeSources: IncomeSource[];
  onClose: () => void;
  onConfirm: (draft: OperationDraft, transactionId?: string) => Promise<void>;
}

const TYPE_LABELS: Record<OperationDraft["type"], string> = {
  expense: "Расход",
  income: "Доход",
  transfer: "Перевод",
  opening_balance: "Начальный остаток",
  balance_adjustment: "Корректировка",
  refund: "Возврат",
};

function rubleInput(amountMinor: bigint): string {
  const sign = amountMinor < 0n ? "-" : "";
  const absolute = amountMinor < 0n ? -amountMinor : amountMinor;
  const kopecks = absolute % 100n;
  return `${sign}${absolute / 100n}${kopecks ? `.${kopecks.toString().padStart(2, "0")}` : ""}`;
}

export function OperationModal({
  initialDraft,
  transactionId,
  accounts,
  categories,
  incomeSources,
  onClose,
  onConfirm,
}: OperationModalProps) {
  const [draft, setDraft] = useState(initialDraft);
  const [editing, setEditing] = useState(Boolean(missingField(initialDraft)));
  const [amount, setAmount] = useState(rubleInput(initialDraft.amountMinor));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeAccounts = useMemo(
    () => accounts.filter((account) => !account.archivedAt || draftAccountIds(draft).includes(account.accountId)),
    [accounts, draft],
  );

  const accountName = (id: string | null | undefined) => accounts.find((account) => account.accountId === id)?.name ?? "Не выбран";
  const categoryName = categories.find((category) => category.id === draft.categoryId)?.name ?? "Другое";
  const sourceName = incomeSources.find((source) => source.id === draft.incomeSourceId)?.name ?? "Другое";
  const needsDestination = draft.type === "transfer";

  async function save(event?: FormEvent) {
    event?.preventDefault();
    setError(null);
    let amountMinor: bigint;
    try {
      amountMinor = parseRubles(amount);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Некорректная сумма");
      return;
    }
    const next = { ...draft, amountMinor };
    const missing = missingField(next);
    if (missing) {
      setError(missing === "amount" ? "Укажите сумму" : missing === "toAccount" ? "Укажите счёт зачисления" : "Укажите счёт");
      return;
    }
    if (next.type === "transfer" && next.accountId === next.toAccountId) {
      setError("Для перевода нужны два разных счёта");
      return;
    }
    setDraft(next);
    if (editing) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onConfirm(next, transactionId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось сохранить операцию");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop is-open" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="confirm-card" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <button className="modal-close" type="button" onClick={onClose} aria-label="Закрыть">×</button>
        <span className="recognition-badge"><span className="status-dot" /> {editing ? "Ручная проверка" : `Распознано · ${draft.confidence ?? "Высокая"}`}</span>
        <h2 id="confirm-title">{editing ? "Проверьте поля" : transactionId ? "Сохранить исправление?" : "Всё верно?"}</h2>

        {editing ? (
          <form className="edit-form" onSubmit={save}>
            <label>
              Тип
              <select
                value={draft.type}
                onChange={(event) => setDraft({ ...draft, type: event.target.value as OperationDraft["type"] })}
              >
                {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>
              Сумма, ₽
              <input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} required />
            </label>
            <label>
              {draft.type === "income" || draft.type === "refund" ? "На счёт" : "Со счёта"}
              <select value={draft.accountId} onChange={(event) => setDraft({ ...draft, accountId: event.target.value })} required>
                <option value="">Выберите счёт</option>
                {activeAccounts.map((account) => <option key={account.accountId} value={account.accountId}>{account.name}</option>)}
              </select>
            </label>
            {needsDestination && (
              <label>
                На счёт
                <select value={draft.toAccountId ?? ""} onChange={(event) => setDraft({ ...draft, toAccountId: event.target.value })} required>
                  <option value="">Выберите счёт</option>
                  {activeAccounts.map((account) => <option key={account.accountId} value={account.accountId}>{account.name}</option>)}
                </select>
              </label>
            )}
            {draft.type === "expense" && (
              <label>
                Категория расхода
                <select value={draft.categoryId ?? ""} onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })} required>
                  {categories.filter((category) => !category.archivedAt).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                </select>
              </label>
            )}
            {draft.type === "income" && (
              <label>
                Источник дохода
                <select value={draft.incomeSourceId ?? ""} onChange={(event) => setDraft({ ...draft, incomeSourceId: event.target.value })} required>
                  {incomeSources.filter((source) => !source.archivedAt).map((source) => <option key={source.id} value={source.id}>{source.name}</option>)}
                </select>
              </label>
            )}
            {draft.type === "opening_balance" || draft.type === "balance_adjustment" ? (
              <label>
                Направление
                <select value={draft.direction ?? 1} onChange={(event) => setDraft({ ...draft, direction: Number(event.target.value) as -1 | 1 })}>
                  <option value="1">Увеличить остаток</option>
                  <option value="-1">Уменьшить остаток</option>
                </select>
              </label>
            ) : null}
            <label>
              Дата
              <input type="date" max={todayLocal()} value={draft.occurredOn} onChange={(event) => setDraft({ ...draft, occurredOn: event.target.value })} required />
            </label>
            <label className="full-field">
              Описание
              <input value={draft.description ?? ""} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
            </label>
            {error && <p className="form-message full-field">{error}</p>}
            <div className="confirm-actions full-field">
              <button className="secondary-button" type="button" onClick={onClose}>Отмена</button>
              <button className="confirm-primary" type="submit">Проверить</button>
            </div>
          </form>
        ) : (
          <>
            <p className={`confirm-amount ${draft.type}`}>{formatMinor(draft.type === "expense" ? -draft.amountMinor : draft.amountMinor, draft.type === "income")}</p>
            <div className="confirm-details">
              <div className="confirm-row"><span>Операция</span><strong>{TYPE_LABELS[draft.type]}</strong></div>
              <div className="confirm-row"><span>{needsDestination ? "Направление" : "Счёт"}</span><strong>{needsDestination ? `${accountName(draft.accountId)} → ${accountName(draft.toAccountId)}` : accountName(draft.accountId)}</strong></div>
              {draft.type === "expense" && <div className="confirm-row"><span>Категория</span><strong>{categoryName}</strong></div>}
              {draft.type === "income" && <div className="confirm-row"><span>Источник</span><strong>{sourceName}</strong></div>}
              {draft.type === "refund" && <div className="confirm-row"><span>Категория</span><strong>Наследуется от исходного расхода</strong></div>}
              <div className="confirm-row"><span>Дата</span><strong>{new Date(`${draft.occurredOn}T00:00:00`).toLocaleDateString("ru-RU")}</strong></div>
            </div>
            {needsDestination && <p className="transfer-note">Общий капитал и расходы не изменятся.</p>}
            {error && <p className="form-message">{error}</p>}
            <div className="confirm-actions">
              <button className="secondary-button" type="button" onClick={() => setEditing(true)}>Исправить</button>
              <button className="confirm-primary" disabled={busy} type="button" onClick={() => void save()}>{busy ? "Сохраняю…" : "Подтвердить"}</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function draftAccountIds(draft: OperationDraft): string[] {
  return [draft.accountId, draft.toAccountId].filter(Boolean) as string[];
}
